import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Container,
  IconButton,
  Badge,
  Fade,
  TextField,
  InputAdornment,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  ArrowBack as ArrowBackIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate, useParams, useNavigationType } from "react-router-dom";
import Header from "../../Components/Common/Header";
import Footer from "../../Components/Common/Footer";
import axios from "axios";
import config from "../../config/config";
import { logUserAction } from "../../utils/analytics";
import NewOnSaleProductCard from "../../Components/Product/NewOnSaleProductCard";
import { brandLogoMap } from "../../assets/dummyData";
import FilterSidebar from "../../Components/NewOnSale/FilterSidebar";
import SortDropdown from "../../Components/NewOnSale/SortDropdown";
import CategoryPills from "../../Components/Product/CategoryPills";
import {
  countActiveFilters,
  hasActiveFilters,
  SORT_OPTIONS,
  MAX_PRICE_DEFAULT,
} from "../../utils/filterUtils";

const AiSearchBar = ({ brandName, themeColor, currentQuery, onSearch, placeholder }) => {
  const [localInput, setLocalInput] = useState(currentQuery);

  useEffect(() => {
    setLocalInput(currentQuery);
  }, [currentQuery]);

  return (
    <TextField
      fullWidth
      variant="outlined"
      placeholder={placeholder}
      value={localInput}
      onChange={(e) => setLocalInput(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onSearch(localInput);
        }
      }}
      InputProps={{
        sx: {
          borderRadius: 8,
          backgroundColor: "#fff",
          boxShadow: "0px 2px 10px rgba(0,0,0,0.05)",
          "& fieldset": { borderColor: "transparent" },
          "&:hover fieldset": { borderColor: themeColor },
          "&.Mui-focused fieldset": { borderColor: themeColor },
          "& input": { fontSize: { xs: "12px", sm: "16px" } },
        },
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: themeColor }} />
          </InputAdornment>
        ),
        endAdornment: localInput ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => {
                setLocalInput("");
                onSearch("");
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
};

const ViewAllProductsPage = () => {
  const { brandParam } = useParams();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const location = useLocation();

  useEffect(() => {
    if (navigationType !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, navigationType]);

  // Resolve brand name: try brandLogoMap first (case-insensitive, slug-aware), else use raw param
  const decodedParam = decodeURIComponent(brandParam || "");
  const matchedBrand = brandLogoMap.find(b =>
    b.name.toLowerCase() === decodedParam.toLowerCase() ||
    b.name.toLowerCase().replace(/\s+/g, '-') === decodedParam.toLowerCase()
  );
  // For View All, allow ANY brand — not restricted to brandLogoMap
  const brandName = matchedBrand ? matchedBrand.name : decodedParam.replace(/-/g, ' ');
  const title = `${brandName} - All Products`;
  const type = "all";

  const [prevBrand, setPrevBrand] = useState(brandName);
  const [activeCategory, setActiveCategory] = useState(() => sessionStorage.getItem(`activeCategory_${brandName}_all`) || "ALL");

  if (brandName !== prevBrand) {
    setPrevBrand(brandName);
    // Note: We can't use setActiveFilters or setActiveSort here yet since they aren't defined,
    // but they reset via their own initializers or we can reset them in useEffect.
    setActiveCategory("ALL");
    sessionStorage.setItem(`activeCategory_${brandName}_all`, "ALL");
  }

  const loggedBrandRef = useRef(null);

  const trackingContext = useMemo(() => ({
    source: "view_all_page",
    category: activeCategory !== "ALL" ? activeCategory : "",
  }), [activeCategory]);

  useEffect(() => {
    if (brandName && loggedBrandRef.current !== brandName) {
      loggedBrandRef.current = brandName;
      logUserAction({
        action: "view_all_visit",
        data: {
          brand: brandName,
        }
      });

      // Also log as brand impression
      import("../../utils/analytics").then(({ logBatchUserAction }) => {
        logBatchUserAction([{
          action: "brand_impression",
          data: {
            brand: brandName,
            ...trackingContext
          }
        }]);
      });
    }
  }, [brandName, trackingContext]);

  // === Product Data State ===
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const LIMIT = 20;

  // === Filter/Sort State ===
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ categories: [], variants: {} });
  const [activeFilters, setActiveFilters] = useState(() => {
    const saved = sessionStorage.getItem(`activeFilters_${brandName}_all`);
    return saved ? JSON.parse(saved) : null;
  });
  const [activeSort, setActiveSort] = useState(() => {
    return sessionStorage.getItem(`activeSort_${brandName}_all`) || SORT_OPTIONS.MOST_RELEVANT;
  });

  useEffect(() => {
    sessionStorage.setItem(`activeFilters_${brandName}_all`, JSON.stringify(activeFilters));
  }, [activeFilters, brandName]);

  useEffect(() => {
    sessionStorage.setItem(`activeSort_${brandName}_all`, activeSort);
  }, [activeSort, brandName]);

  // === AI Search State ===
  const [aiSearchQuery, setAiSearchQuery] = useState(() => {
    return sessionStorage.getItem(`aiSearchQuery_${brandName}_all`) || "";
  });

  useEffect(() => {
    sessionStorage.setItem(`aiSearchQuery_${brandName}_all`, aiSearchQuery);
  }, [aiSearchQuery, brandName]);

  // Clear filters/sort when brand changes
  useEffect(() => {
    if (brandName !== prevBrand) {
      setActiveFilters(null);
      setActiveSort(SORT_OPTIONS.MOST_RELEVANT);

      // Load search and filter state for the new brand, or clear it
      const savedFilters = sessionStorage.getItem(`activeFilters_${brandName}_all`);
      setActiveFilters(savedFilters ? JSON.parse(savedFilters) : null);
      setActiveSort(sessionStorage.getItem(`activeSort_${brandName}_all`) || SORT_OPTIONS.MOST_RELEVANT);

      // Load search state for the new brand, or clear it
      setAiSearchQuery(sessionStorage.getItem(`aiSearchQuery_${brandName}_all`) || "");
    }
  }, [brandName, prevBrand]);

  const observer = useRef();
  const currentRequestRef = useRef(0);

  // Cache the default view, AI search view, or filtered view
  const getFiltersString = (filters, sort) => {
    if (!filters && sort === SORT_OPTIONS.MOST_RELEVANT) return 'default';
    return btoa(encodeURIComponent(JSON.stringify({ f: filters, s: sort }))).replace(/=/g, '');
  };
  const cacheKey = `brandProducts_${brandName}_all_${activeCategory}_${aiSearchQuery || 'default'}_${getFiltersString(activeFilters, activeSort)}`;

  // Theme color — orange
  const themeColor = "#FF6B00";

  // Redirect if accessed without brand param
  useEffect(() => {
    if (!brandName) {
      navigate("/");
    }
  }, [brandName, navigate]);

  // Fetch Filter Options once on mount
  useEffect(() => {
    if (brandName) {
      axios.get(`${config.backendUrl}/inventory/brand_filter_options`, {
        params: { brand: brandName, type: "all" }
      }).then(res => {
        setFilterOptions(res.data.results || { categories: [], variants: {} });
      }).catch(err => console.error("Error fetching filter options", err));
    }
  }, [brandName]);

  // === Data Fetching ===
  const fetchProducts = useCallback(async (currentOffset, reset = false, currentFilters = null, currentSort = null, currentCat = null) => {
    const requestId = ++currentRequestRef.current;

    try {
      if (reset) {
        setProducts([]);
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const filtersToUse = currentFilters !== null ? currentFilters : activeFilters;
      const sortToUse = currentSort !== null ? currentSort : activeSort;
      const catToUse = currentCat !== null ? currentCat : activeCategory;

      let requestBody = {
        brand: brandName,
        limit: aiSearchQuery ? 100 : LIMIT,
        offset: currentOffset,
      };

      if (aiSearchQuery) {
        requestBody.query = aiSearchQuery;
        requestBody.type = "all";
      }

      if (catToUse !== "ALL") {
        requestBody.category_l2 = catToUse;
      }

      if (sortToUse !== SORT_OPTIONS.MOST_RELEVANT) {
        requestBody.sort_by = sortToUse;
      }

      if (filtersToUse) {
        if (filtersToUse.priceRange) {
          const minLimit = Math.floor(filterOptions?.min_price || 0);
          let maxLimit = Math.ceil(filterOptions?.max_price || MAX_PRICE_DEFAULT);
          if (maxLimit <= minLimit) maxLimit = minLimit + 1000;

          const isPriceAltered = filtersToUse.priceRange[0] > minLimit || filtersToUse.priceRange[1] < maxLimit;

          if (isPriceAltered) {
            requestBody.min_price = filtersToUse.priceRange[0];
            requestBody.max_price = filtersToUse.priceRange[1];
          }
        }
        if (filtersToUse.categories?.length > 0) {
          requestBody.categories = filtersToUse.categories;
        }
        if (filtersToUse.variants && Object.keys(filtersToUse.variants).length > 0) {
          requestBody.variants = filtersToUse.variants;
        }
        if (filtersToUse.showNullPriceOnly) {
          requestBody.show_null_price_only = true;
        }
      }

      let endpoint = "brand_all_products";
      if (aiSearchQuery) {
        endpoint = "brand_ai_search";
      }

      const response = await axios.post(`${config.backendUrl}/inventory/${endpoint}`, requestBody);

      if (currentRequestRef.current !== requestId) {
        return { newProducts: [], moreAvailable: false };
      }

      const newProducts = response.data?.results?.products || [];
      const moreAvailable = aiSearchQuery ? false : (response.data?.results?.has_more || false);

      setProducts((prev) => {
        const finalProducts = currentOffset === 0 ? newProducts : [...prev, ...newProducts];

        // Cache the current view for scroll restoration
        const targetCacheKey = `brandProducts_${brandName}_all_${catToUse}_${aiSearchQuery || 'default'}_${getFiltersString(filtersToUse, sortToUse)}`;
        sessionStorage.setItem(targetCacheKey, JSON.stringify({
          products: finalProducts,
          offset: currentOffset,
          hasMore: moreAvailable,
        }));

        return finalProducts;
      });

      setHasMore(moreAvailable);
      return { newProducts, moreAvailable };
    } catch (error) {
      if (currentRequestRef.current !== requestId) return { newProducts: [], moreAvailable: false };
      console.error("Error fetching brand products:", error);
      if (reset) setProducts([]);
      return { newProducts: [], moreAvailable: false };
    } finally {
      if (currentRequestRef.current === requestId) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [brandName, activeFilters, activeSort, activeCategory, cacheKey, filterOptions, aiSearchQuery]);

  // Initial load
  useEffect(() => {
    if (navigationType !== "POP") {
      window.scrollTo(0, 0);
    }
    if (brandName) {
      // Check cache first for scroll restoration
      const cachedData = sessionStorage.getItem(cacheKey);
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          setProducts(parsed.products);
          setOffset(parsed.offset);
          setHasMore(parsed.hasMore);
          setLoading(false);
          return;
        } catch (e) {
          console.error("Failed to parse cached data", e);
        }
      }

      setOffset(0);
      setHasMore(true);
      fetchProducts(0, true, activeFilters, activeSort, activeCategory);
    }
  }, [brandName, cacheKey, fetchProducts, activeFilters, activeSort, activeCategory, navigationType, aiSearchQuery]);

  // === Infinite Scroll ===
  const loadMore = () => {
    if (!loadingMore && hasMore) {
      const nextOffset = offset + LIMIT;
      setOffset(nextOffset);
      fetchProducts(nextOffset, false);
    }
  };

  const lastProductElementRef = useCallback(
    (node) => {
      if (loading || loadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, loadingMore, hasMore]
  );

  // === Filter/Sort Handlers ===
  const handleApplyFilters = useCallback(
    (filters) => {
      setActiveFilters(filters);
    },
    []
  );

  const handleClearFilters = useCallback(() => {
    setActiveFilters(null);
  }, []);

  const handleSortChange = useCallback(
    (sortOption) => {
      setActiveSort(sortOption);
    },
    []
  );

  const handleCategoryChange = useCallback((newCat) => {
    setActiveCategory(newCat);
    sessionStorage.setItem(`activeCategory_${brandName}_all`, newCat);
    setOffset(0);
    setHasMore(true);
    fetchProducts(0, true, activeFilters, activeSort, newCat);
  }, [activeFilters, activeSort, fetchProducts, brandName]);

  const activeFilterCount = useMemo(() => {
    const minPriceLimit = Math.floor(filterOptions?.min_price || 0);
    let maxPriceLimit = Math.ceil(filterOptions?.max_price || MAX_PRICE_DEFAULT);
    if (maxPriceLimit <= minPriceLimit) {
      maxPriceLimit = minPriceLimit + 1000;
    }
    return countActiveFilters(activeFilters, minPriceLimit, maxPriceLimit);
  }, [activeFilters, filterOptions]);

  const renderedProductGrid = useMemo(() => (
    <Grid container spacing={3}>
      {products.map((product, index) => {
        const isLast = products.length === index + 1;
        return (
          <Grid
            item
            xs={6}
            sm={6}
            md={4}
            lg={3}
            xl={2}
            key={`${product.product_id || index}-${index}`}
            ref={isLast ? lastProductElementRef : undefined}
          >
            <NewOnSaleProductCard product={product} noColor trackingContext={trackingContext} />
          </Grid>
        );
      })}
    </Grid>
  ), [products, lastProductElementRef, trackingContext]);

  if (!brandName) return null;

  const brandData = brandLogoMap.find((b) => b.name === brandName);
  const logoSrc = brandData ? brandData.logo : null;
  const scale = brandData?.scale || 1;

  return (
    <Box sx={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 50%, #FFFFFF 100%)", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Header />

      <Container maxWidth="xl" sx={{ pt: { xs: 4, md: 1 }, pb: 4, flexGrow: 1 }}>
        <Box sx={{ position: "relative", mb: 6 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              bgcolor: "rgba(0,0,0,0.02)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.06)" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              mb: 2,
              mx: -3,
            }}
          >
            <Box
              onClick={() => navigate("/brands")}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                backgroundColor: "rgba(255, 107, 0, 0.06)",
                border: "1px solid rgba(255, 107, 0, 0.2)",
                borderRadius: "100px",
                px: { xs: 2, md: 2.5 },
                py: { xs: 0.75, md: 1 },
                mb: { xs: 2, md: 0 },
                position: { xs: "relative", md: "absolute" },
                left: { md: 64 },
                cursor: "pointer",
                transition: "all 0.3s ease",
                zIndex: 10,
                "&:hover": {
                  backgroundColor: "rgba(255, 107, 0, 0.12)",
                  transform: { xs: "translateY(-2px)", md: "translateY(-50%) scale(1.02)" },
                  boxShadow: "0 4px 12px rgba(255, 107, 0, 0.15)",
                },
                ...({ md: { top: "50%", transform: "translateY(-50%)" } })
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FF6B00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
              </svg>
              <Typography
                sx={{
                  fontFamily: "Poppins-Medium",
                  fontSize: { xs: "12px", md: "13px" },
                  color: "#FF6B00",
                  letterSpacing: "0.5px",
                  whiteSpace: "nowrap",
                }}
              >
                View All Brands
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative", width: "100%", height: { xs: "60px", md: "auto" }, mt: { xs: 2, md: 0 } }}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                {logoSrc ? (
                  <Box sx={{ height: { xs: "50px", md: "70px" }, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img
                      src={logoSrc}
                      alt={brandName}
                      style={{
                        maxHeight: "100%",
                        maxWidth: "250px",
                        objectFit: "contain",
                        transform: `scale(${scale})`,
                      }}
                    />
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: "Poppins-SemiBold",
                      fontSize: { xs: "24px", md: "28px" },
                      color: "#111",
                      letterSpacing: "0.5px"
                    }}
                  >
                    {brandName}
                  </Typography>
                )}
              </Box>

              <Box sx={{
                position: "absolute",
                right: { xs: 16, sm: 24, md: 24 },
                top: "50%",
                transform: "translateY(-50%)"
              }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontFamily: "'Lora', serif",
                    fontSize: { xs: "13px", md: "16px" },
                    color: "#FF6B00",
                    fontWeight: 500,
                    letterSpacing: "-0.02em",
                    fontStyle: "italic",
                  }}
                >
                  All Products
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* === Filter, Categories & Sort Toolbar === */}
        <Fade in={!loading || products.length > 0} timeout={500}>
          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                mb: 3,
                px: { xs: 0, sm: 1 },
              }}
            >
              {/* Filter Button */}
              <Box
                onClick={() => setFilterOpen(true)}
                sx={{
                  order: { xs: 2, md: 1 },
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  cursor: "pointer",
                  px: 2,
                  py: 1,
                  borderRadius: "12px",
                  border: `1.5px solid ${activeFilterCount > 0 ? themeColor : "#E0E0E0"}`,
                  background: activeFilterCount > 0 ? `${themeColor}08` : "#fff",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  userSelect: "none",
                  "&:hover": {
                    borderColor: themeColor,
                    background: "rgba(255, 107, 0, 0.08)",
                    transform: "translateY(-1px)",
                    boxShadow: `0 4px 12px ${themeColor}15`,
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
              >
                <Badge
                  badgeContent={activeFilterCount}
                  sx={{
                    "& .MuiBadge-badge": {
                      background: "linear-gradient(135deg, #FF8C33 0%, #FF6B00 100%)",
                      color: "#fff",
                      fontSize: "10px",
                      fontFamily: "Poppins-Bold",
                      minWidth: "18px",
                      height: "18px",
                      top: -2,
                      right: -4,
                    },
                  }}
                >
                  <FilterListIcon
                    sx={{
                      fontSize: 18,
                      color: activeFilterCount > 0 ? themeColor : "#888",
                      transition: "color 0.2s ease",
                    }}
                  />
                </Badge>
                <Typography
                  sx={{
                    fontFamily: "Poppins-Medium",
                    fontSize: "13px",
                    color: activeFilterCount > 0 ? "#333" : "#666",
                    whiteSpace: "nowrap",
                    ml: activeFilterCount > 0 ? 0.5 : 0,
                    transition: "all 0.2s ease",
                  }}
                >
                  Filters
                </Typography>
              </Box>

              <Box sx={{ order: { xs: 1, md: 2 }, width: { xs: "100%", md: "auto" }, display: "flex", justifyContent: "center" }}>
                <CategoryPills activeCategory={activeCategory} onChange={handleCategoryChange} />
              </Box>

              <Box sx={{ order: { xs: 3, md: 3 }, flexShrink: 0 }}>
                <SortDropdown activeSort={activeSort} onSortChange={handleSortChange} />
              </Box>
            </Box>

            {/* AI Search Bar */}
            <Box sx={{ maxWidth: 600, mx: "auto", mb: 4, px: 2 }}>
              <AiSearchBar
                brandName={brandName}
                themeColor={themeColor}
                currentQuery={aiSearchQuery}
                onSearch={(query) => {
                  setAiSearchQuery(query);
                  if (query && query.trim() !== "") {
                    logUserAction({
                      action: "search",
                      data: {
                        keyword: query.trim(),
                        gender: null,
                        brandsearch_label: `View All ${brandName}`,
                      },
                      timestamp: new Date().toISOString(),
                    });
                  }
                }}
                placeholder={`AI Search across these ${brandName} products`}
              />
            </Box>
          </Box>
        </Fade>

        {/* Main Product Grid */}
        {loading && products.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
            <CircularProgress style={{ color: themeColor }} />
          </Box>
        ) : (
          <>
            {products.length === 0 ? (
              // Filters active but no matching products
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 8,
                }}
              >
                <FilterListIcon sx={{ fontSize: 48, color: "#ddd", mb: 2 }} />
                <Typography
                  sx={{
                    fontFamily: "Poppins-Medium",
                    fontSize: "16px",
                    color: "#999",
                    textAlign: "center",
                    mb: 1,
                  }}
                >
                  No products match your filters
                </Typography>
                <Typography
                  onClick={() => {
                    setActiveFilters(null);
                    setActiveSort(SORT_OPTIONS.MOST_RELEVANT);
                  }}
                  sx={{
                    fontFamily: "Poppins-Medium",
                    fontSize: "14px",
                    color: themeColor,
                    cursor: "pointer",
                    textAlign: "center",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Clear all filters
                </Typography>
              </Box>
            ) : (
              renderedProductGrid
            )}

            {loadingMore && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 4 }}>
                <CircularProgress size={30} style={{ color: themeColor }} />
              </Box>
            )}

            {!hasMore && products.length > 0 && (
              <Typography
                sx={{
                  textAlign: "center",
                  mt: 6,
                  mb: 4,
                  color: "#888",
                  fontFamily: "Poppins-Medium",
                }}
              >
                No more products to show
              </Typography>
            )}
          </>
        )}
      </Container>

      {/* Filter Sidebar */}
      <FilterSidebar
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filterOptions={filterOptions}
        activeFilters={activeFilters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        type={type}
        brandName={brandName}
      />

      <Footer />
    </Box>
  );
};

export default ViewAllProductsPage;
